import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import OptionCarousel from "./OptionSwiperCarouselNewUI";
import AIImagePreview from "./AIImagePreview";
import "../styles/fashion_styles.css";

import {
  Box,
  Button,
  Icon,
  Typography,
  TextField,
  Alert,
  Badge,
  Chip,
  Divider,
  LinearProgress,
  Card,
  Skeleton,
  CardHeader,
  CardContent,
  CardActionArea,
  styled,
  Paper,
  Collapse,
  Menu,
  MenuItem,
  CheckIcon,
} from "../utils/mui-imports";
import { Mousewheel, Thumbs } from "swiper/modules";
import {
  FaArrowAltCircleDown,
  FaArrowCircleRight,
  FaArrowDown,
  FaArrowRight,
  FaChevronCircleLeft,
  FaChevronDown,
  FaChevronRight,
  FaEdit,
  FaHome,
  FaRegArrowAltCircleRight,
  FaSearch,
  FaSearchPlus,
  FaUser,
} from "react-icons/fa";
import { AutoAwesomeIcon } from "../utils/mui-imports";
import { EditIcon } from "../utils/mui-imports";
import { FiArrowUpRight, FiChevronLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Caption from "./Caption";
import Toastbox from "./Toastbox";
import { trackGAEvent } from '../utils/tracking';

const requiredAttributes = [
  "gender",
  "type",
  "occasion",
  "category",
  "style",
  "silhouette",
  "palette",
  "pattern",
  "fabric",
];

const CssTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "black",
    fontWeight: "bold",
    textAlign: "center",
  },
  "& label": {
    fontSize: "0.8rem",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#B2BAC2",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#E0E3E7",
    },
    "&:hover fieldset": {
      borderColor: "#B2BAC2",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6F7E8C",
    },
    "& .MuiInputBase-input": {
      color: "#000000",
      fontSize: "1rem",
      fontWeight: "bold",
      p: 2,
    },
  },
});

const fetchSmartOptions = async (attribute, user_input) => {
  try {
    const gender = user_input.gender;
    // console.log(`📡 Fetching options for ${attribute} under type: ${user_input.attribute}`);
    const response = await fetch("/api/smart_attributes_via_gcs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attribute, user_input, gender }),
    });

    if (!response.ok) throw new Error(`Failed to fetch ${attribute}`);
    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${attribute} options:`, error);
    return { options: [] };
  }
};

const FashionStyleBuilder = ({user, credits, isUser, status}) => {
  const base_gcs_uri =
    "https://storage.googleapis.com/buzzin-public-access/the-look/curate-look-choices";
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [attributeOptions, setAttributeOptions] = useState({});
  const [caption, setCaption] = useState(null);
  const [title, setTitle] = useState(null);
  const [lookDescription, setLookDescription] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [tagsPosition] = useState("top");
  const [anchorEl, setAnchorEl] = useState(null);
  const [dropdownAttr, setDropdownAttr] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [FashionTips, setFashionTips] = useState({
    // type: "Custom Text to provide assistance at this step.",
    // gender: "Custom Text to provide assistance at this step.",
    // occasion: "Custom Text to provide assistance at this step.",
  });
  const [suggestedOptions, setSuggestedOptions] = useState({})
  const swiperRef = useRef(null);
  const navigate = useNavigate();
  const [isGenerated, setIsGenerated] = useState(false);
  const isMobile = window.innerWidth < 768

  useEffect(() => {
    setAttributeOptions({
      type: ["western", "ethnic"],
      gender: ["men", "women"],
    });
  }, []);

  const handleAttributeChange = async (key, value) => {
    const currentIndex = requiredAttributes.indexOf(key);
    const updatedAttributes = { ...selectedAttributes };
    try{
      trackGAEvent('look_attribute_selected', { user: user, key: key, value: value });
    } catch{
      console.log("Ignore tracking");
    }

    for (let i = currentIndex; i < requiredAttributes.length; i++) {
      delete updatedAttributes[requiredAttributes[i]];
    }
    updatedAttributes[key] = value;
    setSelectedAttributes(updatedAttributes);

    if (currentIndex < requiredAttributes.length - 1) {
      const nextAttribute = requiredAttributes[currentIndex + 1];
      const response = await fetchSmartOptions(nextAttribute, updatedAttributes);
      setAttributeOptions((prev) => ({ ...prev, [nextAttribute]: response.options }));
      if (response.suggestion) {
        setFashionTips((prev) => ({ ...prev, [nextAttribute]: response.suggestion.fashion_tip }));
        setSuggestedOptions((prev) => ({ ...prev, [nextAttribute]: response.suggestion.suggested_options }));
      }
    } else if (currentIndex === requiredAttributes.length - 1) {
      const finalResponse = await fetchSmartOptions("summary", updatedAttributes);
      if (finalResponse.suggestion) {
        setCaption(finalResponse.suggestion.caption);
        setTitle(finalResponse.suggestion.title);
      }
    }
    if (swiperRef.current) swiperRef.current.slideNext();
  };

  const handleSlideChange = (swiper) => {
    setCurrentSlide(swiper.activeIndex);
    const firstUnselectedIndex = requiredAttributes.findIndex(
      (attr) => !selectedAttributes[attr]
    );

    if (firstUnselectedIndex !== -1 && swiper.activeIndex > firstUnselectedIndex) {
      swiper.slideTo(firstUnselectedIndex);
    } else if (swiper.activeIndex > requiredAttributes.length && !isGenerated) {
      swiper.slideTo(requiredAttributes.length);
    } else {
      setActiveStep(swiper.activeIndex);
    }
  };

  const handleTagClick = (index) => {
    if (index < activeStep) {
      const updatedAttributes = { ...selectedAttributes };
      for (let i = index + 1; i < requiredAttributes.length; i++) {
        delete updatedAttributes[requiredAttributes[i]];
      }
      setSelectedAttributes(updatedAttributes);
    }
    swiperRef.current.slideTo(index);
  };

  const handleHeadingClick = (event, attr) => {
    setAnchorEl(event.currentTarget);
    setDropdownAttr(attr);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setDropdownAttr(null);
  };

  const handleNavigate = (index) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
    handleClose();
  };

  const SelectedTagsLastSlide = () => (
    <Box mb={1}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "left",
          // overflowX:'auto'
        }}
      >
        {requiredAttributes.map(
          (attr, index) =>
            selectedAttributes[attr] && (
              <Card
                key={attr}
                variant="outlined"
                sx={{
                  px: 1,
                  py:0.25,
                  borderRadius: 3,
                  justifyContent: "center",
                  color: "black",
                  backgroundColor: "#fff5e6",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                    alignItems: "start",
                    // px: 1,
                  }}
                >
                  <Typography fontSize="0.70rem" textTransform="capitalize" fontWeight="bold">
                    {attr}
                  </Typography>
                  <Typography noWrap fontSize="0.65rem" fontWeight="normal" textTransform="capitalize">
                    {selectedAttributes[attr]}
                  </Typography>
                </Box>
              </Card>
            )
        )}
      </Box>
    </Box>
  );

  const SelectedTagsBottom = () => {
    if (Object.keys(selectedAttributes).length === 0) return null;
    return (
      <Box
        width="100%"
        mt={2}
        sx={{
          backgroundColor: "#fff5e69a",
          bottom: 0,
          position: "relative",
        }}
      >
        <Divider textAlign="left">
          <Chip
          // size='medium'
            sx={{ height: "auto", p: 0.25 }}
            label={
              <Typography fontWeight={'bold'} variant="body1" fontSize="0.75rem">
                Your Curation
              </Typography>
            }
          />
        </Divider>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "top",
            paddingX: 2,
            pt: 1.5,
            height: "10dvh",
            overflowY: "scroll",
            gap: 2,
            px: 4,
          }}
        >
          {requiredAttributes.slice(0, activeStep).map((attr, index) => (
            <Box key={attr} sx={{ display: "flex", flexDirection: "column", gap: 0, alignItems: "start" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <Typography color="black" fontSize="0.70rem" textTransform="capitalize" fontWeight="bold">
                  {attr}
                </Typography>
                <FaChevronRight color="black" size={12} />
              </Box>
              <Typography color="black" noWrap fontSize="0.65rem" fontWeight="normal" textTransform="capitalize">
                {selectedAttributes[attr]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const handleBack = () => {
    if (activeStep > 0) {
      const updatedAttributes = { ...selectedAttributes };
      delete updatedAttributes[requiredAttributes[activeStep]];
      setSelectedAttributes(updatedAttributes);
      setActiveStep(activeStep - 1);
      swiperRef.current.slideTo(activeStep - 1);
    }
  };

  const FixedStepper = ({ activeStep, selectedAttributes, requiredAttributes, onBack }) => {
    const totalSteps = requiredAttributes.length;

    return (
      <Card
        variant="outlined"
        sx={{
          border: "none",
          flexDirection: "column",
          alignItems: "center",
          paddingX: 4,
          height: "10dvh",
          paddingTop:'1rem'
          // backgroundColor:'#fff5e69a'
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Button
          
          startIcon={<FaChevronCircleLeft 
            color={activeStep===0? 'grey': 'black'} 
            size={32} />} 
          disabled={activeStep === 0}  
          onClick={onBack} />
          <Typography
            fontSize="1.75rem"
            fontWeight="bold"
            textTransform="capitalize"
            sx={{ color: "#2e3a59" }}
          >
            {requiredAttributes[activeStep]}
          </Typography>
          <Chip
            variant="outlined"
            label={
              <Typography variant="h6" fontWeight="normal">
                {activeStep + 1} / <strong>{totalSteps}</strong>
              </Typography>
            }
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={((activeStep + 1) / totalSteps) * 100}
          sx={{
            flex: 1,
            my: 1,
            height: 10,
            borderRadius: "15px",
            backgroundColor: "rgba(30, 30, 30, 0.9)",
            "& .MuiLinearProgress-bar": {
              backgroundImage: "linear-gradient(to right, #e01cd5, #1CB5E0)",
              borderRadius: "15px",
            },
          }}
        />
      </Card>
    );
  };



  return (
    <div 
    // className="app-container"
    style={{
      // height: '90vh',
      width: 'auto',
      // marginTop: 0,
      paddingTop: '10dvh'
    }}
    >

        

      {activeStep < requiredAttributes.length && (
        <>
          <FixedStepper
            activeStep={activeStep}
            selectedAttributes={selectedAttributes}
            requiredAttributes={requiredAttributes}
            onBack={handleBack}
          />
          <Box py={1}>
            <Divider textAlign="left">
              <Typography textTransform="capitalize" fontWeight="bold" fontSize="0.85rem">
                Select {requiredAttributes[activeStep]}
              </Typography>
            </Divider>
          </Box>
        </>
      )}

      <Swiper
        slidesPerView={1}
        direction="horizontal"
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        onSlideChange={handleSlideChange}
        allowTouchMove={false}
        speed={250}
        spaceBetween={0}
        style={{
          height: currentSlide + 1 > requiredAttributes.length ? "90dvh" : "60dvh",
          // width: "100vw",
          overflowY: "scroll",
          // backgroundColor:'black'
        }}
      >
        {requiredAttributes.map((attr) => (
          <SwiperSlide key={attr}>
            <div className="slide-content"
            
            style={{

            }}
            >
              <OptionCarousel
              suggestedOptions={suggestedOptions[attr]}
                label={attr}
                options={attributeOptions[attr] || []}
                images={(attributeOptions[requiredAttributes[activeStep]] || []).map((option) => {
                  if (requiredAttributes[activeStep] === "gender") {
                    return `${base_gcs_uri}/style_builder2/gender/${option}.jpg`;
                  } else if (requiredAttributes[activeStep] === "type") {
                    return `${base_gcs_uri}/style_builder2/${requiredAttributes[activeStep]}/${
                      selectedAttributes.gender
                    }/${option}.jpg`;
                  } else {
                    return `${base_gcs_uri}/style_builder3/${selectedAttributes.gender}/${selectedAttributes.type}/${requiredAttributes[activeStep]}/${option}.jpg`;
                  }
                })}
                onSelect={(value) => handleAttributeChange(attr, value)}
                selected={selectedAttributes[attr] ? [selectedAttributes[attr]] : []}
              />
              {FashionTips[attr] ? (
                <Collapse timeout={200} in={requiredAttributes[activeStep] === attr} sx={{ width: "100%" }}>
                  <Alert
                    sx={{
                      mt: 2,
                      height: "auto",
                      maxHeight:'17.5dvh',
                      overflowY: "scroll",
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                      color: "black",
                      
                    }}
                    icon={<AutoAwesomeIcon fontSize="inherit" />}
                    severity='info'
                  >
                     <Caption caption={FashionTips[attr]} numberOfLines={3}/>
                  </Alert>
                 
                </Collapse>
              ) : (
               
                null
              )}
            </div>
          </SwiperSlide>
        ))}


        {/* LAST SLIDE -------------- */}

        <SwiperSlide>
          {requiredAttributes.every((attr) => selectedAttributes[attr]) ? (
            <Card
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "auto",
                backgroundColor: "#ffffff",
                px: 1,
                // py: 1,
              }}
              className="slide-up"
              variant="elevated"
            >
              <CardContent
                sx={{
                  flexGrow: 1,
                  overflowY: "scroll",
                }}
              >
                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    backgroundColor: "#fff5e6",
                    px: 2,
                    pt:2,
                    borderRadius: 4,
                  }}
                >
                  <Box 
                  // pt={2}
                  >
                    <Divider textAlign="left">
                      <Chip
                        label={
                          <Typography fontWeight={600} variant="body1" fontSize="0.7rem">
                            Your Curation
                          </Typography>
                        }
                      />
                    </Divider>
                  </Box>
                  <Typography 
                  fontSize={'2.5rem'}
                  variant="h1" align="left" fontWeight="bold">
                    {title}
                  </Typography>
                  <Caption caption={caption} numberOfLines={1} />
                  <SelectedTagsLastSlide />
                </Box>

                <Box py={2}>
                  <Divider textAlign="left">
                    <Chip
                    icon={<FaChevronDown/>}
                      label={
                        <Typography fontWeight={'bold'} variant="body1" fontSize="0.7rem"
                        
                        >
                          Addional Instructions
                        </Typography>
                      }
                    />
                  </Divider>
                </Box>
                
                <Box px={2}>
                  <CssTextField
                    fullWidth
                    multiline
                    rows={2}
                    value={lookDescription}
                    onChange={(e) => setLookDescription(e.target.value)}
                    sx={{ maxWidth: { xs: "100%", md: "600px" }, marginTop: 1 }}
                    label="(Optional) Any final touches? Add a prompt here.."
                    id="custom-css-outlined-input"
                  />
                </Box>
              </CardContent>
              <Box sx={{ px: 1 }}>
                <Button
                  fullWidth
                  startIcon={<AutoAwesomeIcon/>}
                  sx={{
                    backgroundImage: "linear-gradient(to right, #e01cd5, #1CB5E0)",
                    color: "white",
                    fontWeight: "bold",
                    borderRadius: 2,
                    // maxWidth: { xs: "100%", md: "300px" },
                    textTransform: "capitalize",
                  }}
                  onClick={() => {
                    navigate("/shop-the-look", {
                      replace: true,
                      state: {
                        attributes: selectedAttributes,
                        title,
                        caption,
                        showAIImage: true,
                        lookDescription,
                        gender: selectedAttributes["gender"],
                        credits: credits
                      },
                    });
                    trackGAEvent('generate_look', { user: user, attributes: selectedAttributes });
                  }}
                >
                  Generate Your Look
                </Button>
              </Box>
            </Card>
          ) : (
            <p className="error-message">Please select all attributes.</p>
          )}
        </SwiperSlide>
      </Swiper>

      {/* {(user || credits > 0) && (
        <Box
        position={'absolute'}
        bottom={'20%'}
        zIndex={10}
        >
          
          <Toastbox 
          status={status}
          isUser={isUser}
          credits={credits}
          isDialog={false}
          />

        </Box>
      )} */}

      {activeStep < requiredAttributes.length && <SelectedTagsBottom />}
    </div>
  );
};

export default FashionStyleBuilder;